import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { compact } from "lodash"
import { prop } from "ramda"
import { createSelector } from "reselect"
import type { CategoriesListItem, CategoriesListResponse, CategoryPricingType } from "../../../../../graphql/types"
import { getSelectedLocationOverrideId } from "../../../../common/locationsPicker/selectors"
import { getFilters } from "../../../../filters/selectors"
import { FiltersObject } from "../../../../filters/types"
import type { StateObject } from "../../../../redux/types"
import { onlyVisibleTypes } from "../../../reports/salesByItems/utils"
import {
    getItemsAndCategoriesList,
    getItemsAndCategoriesListResponse,
    getItemsCategoriesSearchValue,
} from "../../selectors"
import { ItemIsActiveFilter, ItemsAndCategoriesTableFilters } from "../../types"
import { computeOverriddenValue, formatCategoryPriceRange, isCateringType } from "../../utils"
import { getNewCategoryColor, getNewCategoryIcon } from "../selectors"
import type { CategoriesTableDataItem } from "./CategoriesTable"
import { getPricingTypeLabel, isAnyChildrenActiveFilterValue } from "./utils"

export const getRootCategory: (state: StateObject) => Maybe<CategoriesListItem> = createSelector(
    getItemsAndCategoriesList,
    itemsAndCategoriesList => {
        const possibleRoots = itemsAndCategoriesList.filter(i => i.parentId == null)

        if (!possibleRoots || possibleRoots.length === 0) {
            return null
        }

        return possibleRoots.find(i => i.name.toLowerCase() === "root") || possibleRoots[0] || null
    }
)

const mapItemsAndCategoriesToTreeData = (
    itemsAndCategoriesData: CategoriesListItem[],
    selectedLocationId: Maybe<string>,
    itemsAndCategoriesResponse: CategoriesListResponse,
    id: string,
    searchKey: Maybe<string>,
    filters: ItemsAndCategoriesTableFilters
): CategoriesTableDataItem[] => {
    return compact(
        itemsAndCategoriesData
            .filter(c => c.parentId === id)
            .map(c => {
                const children = mapItemsAndCategoriesToTreeData(
                    itemsAndCategoriesData,
                    selectedLocationId,
                    itemsAndCategoriesResponse,
                    c.id,
                    searchKey,
                    filters
                )
                const isActiveOverriddenValue = computeOverriddenValue(
                    itemsAndCategoriesResponse.overrides,
                    selectedLocationId,
                    c.id,
                    prop("isActive")
                )
                const priceOverriddenValue = computeOverriddenValue<number[]>(
                    itemsAndCategoriesResponse.overrides,
                    selectedLocationId,
                    c.id,
                    prop("price")
                )

                const isActive = isActiveOverriddenValue != null ? isActiveOverriddenValue : c.isActive

                const isCategoryType = c.type === "category"
                const isCardProgramItem = !!c.cardProgramId || children.some(child => child.isCardProgramItem)

                const result = {
                    id: c.id,
                    name: c.name,
                    type: c.type,
                    icon: getNewCategoryIcon(c.id, itemsAndCategoriesData),
                    color: getNewCategoryColor(c.id, itemsAndCategoriesData),
                    parentId: c.parentId,
                    pricingType: isCategoryType ? null : getCategoriesTablePricingType(c.pricingType, c.unit),
                    price: isCategoryType
                        ? null
                        : priceOverriddenValue != null
                          ? formatCategoryPriceRange(priceOverriddenValue, c.unit)
                          : formatCategoryPriceRange(c.price, c.unit),
                    isActive: isActive,
                    isActiveOverriden: isActiveOverriddenValue != null && isActiveOverriddenValue !== c.isActive,
                    priceOverriden:
                        !isCategoryType &&
                        priceOverriddenValue != null &&
                        JSON.stringify(priceOverriddenValue) !== JSON.stringify(c.price),
                    children: children && children.length > 0 ? children : null,
                    isCardProgramItem: isCardProgramItem,
                    isSelectable: !isCardProgramItem,
                }

                // Check whether item is active or inactive (according to filter)
                // But if any child is active or inactive (according to filter), we don't want to filter out the parent
                const isAnyChildActiveFilterValue = isAnyChildrenActiveFilterValue(
                    children,
                    filters,
                    selectedLocationId,
                    itemsAndCategoriesResponse.overrides
                )
                if (
                    selectedLocationId &&
                    !isAnyChildActiveFilterValue &&
                    ((filters.isActive === ItemIsActiveFilter.Active && !isActive) ||
                        (filters.isActive === ItemIsActiveFilter.Inactive && isActive))
                ) {
                    return null
                }

                if (searchKey == null) {
                    return result
                }

                const searchLower = searchKey.toLowerCase()
                const nameMatch = c.name.toLowerCase().includes(searchLower)

                const pricingTypeLabel = getPricingTypeLabel(result.pricingType)
                const pricingTypeMatch = pricingTypeLabel ? pricingTypeLabel.toLowerCase().includes(searchLower) : false
                const priceMatch = result.price ? result.price.toLowerCase().includes(searchLower) : false

                return (children && children.length > 0) || nameMatch || pricingTypeMatch || priceMatch ? result : null
            })
    ).filter(a => a)
}

export const getDataForCategoriesTable: (
    state: StateObject,
    itemsAndCategoriesFilters: ItemsAndCategoriesTableFilters
) => CategoriesTableDataItem[] = createSelector(
    getSelectedLocationOverrideId,
    getItemsAndCategoriesListResponse,
    getRootCategory,
    getItemsCategoriesSearchValue,
    state => getFilters(state, "itemsAndCategories"),
    (_, itemsAndCategoriesFilter: ItemsAndCategoriesTableFilters) => itemsAndCategoriesFilter,
    (
        selectedLocationId: Maybe<string>,
        itemsAndCategoriesResponse: Maybe<CategoriesListResponse>,
        rootCategory: Maybe<CategoriesListItem>,
        searchValue: Maybe<string>,
        filters: FiltersObject,
        itemsAndCategoriesFilter: ItemsAndCategoriesTableFilters
    ) => {
        if (!rootCategory || !itemsAndCategoriesResponse) {
            return []
        }

        const data = mapItemsAndCategoriesToTreeData(
            itemsAndCategoriesResponse.data || [],
            selectedLocationId,
            itemsAndCategoriesResponse,
            rootCategory.id,
            searchValue,
            itemsAndCategoriesFilter
        )

        if (filters && filters.view) {
            return onlyVisibleTypes(data, filters.view)
        }

        return data
    }
)

export const getCategoriesTablePricingType = (pricingType: Maybe<CategoryPricingType>, unit: Maybe<string>) => {
    if (unit && isCateringType(unit)) {
        return "catering"
    }

    return pricingType
}
